import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { 
  Users, Search, User, Calendar, MapPin, Phone, Mail,
  Briefcase, GraduationCap, FileText, ChevronRight,
  CheckCircle2, Clock, AlertCircle, Building2, Download, FileDown
} from 'lucide-react';
import api, { contratService } from '../services/api';
import { toast } from 'sonner';

const BeneficiairesFinanceur = () => {
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedBeneficiaire, setSelectedBeneficiaire] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedContrats, setSelectedContrats] = useState([]);
  const [downloadingContrats, setDownloadingContrats] = useState(false);

  useEffect(() => {
    loadBeneficiaires();
  }, []);

  const loadBeneficiaires = async () => {
    try {
      const res = await api.get('/financeur/beneficiaires');
      setBeneficiaires(res.data || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (apprenantId) => {
    setLoadingDetail(true);
    try {
      const res = await api.get(`/financeur/beneficiaires/${apprenantId}`);
      setDetailData(res.data);
    } catch (error) {
      console.error('Erreur chargement détail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSelectBeneficiaire = (ben) => {
    setSelectedBeneficiaire(ben);
    setSelectedContrats([]); // Reset sélection lors du changement de bénéficiaire
    loadDetail(ben.id);
  };

  // Téléchargement d'un contrat individuel
  const handleDownloadContrat = async (contratId) => {
    try {
      const response = await contratService.download(contratId);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrat_${contratId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Téléchargement réussi');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Téléchargement groupé de contrats
  const handleDownloadSelected = async () => {
    if (selectedContrats.length === 0) {
      toast.error('Sélectionnez au moins un contrat');
      return;
    }
    
    setDownloadingContrats(true);
    try {
      const response = await contratService.downloadMultiple(selectedContrats);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'contrats_export.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`${selectedContrats.length} contrat(s) téléchargé(s)`);
      setSelectedContrats([]);
    } catch (error) {
      toast.error('Erreur lors du téléchargement groupé');
    } finally {
      setDownloadingContrats(false);
    }
  };

  const toggleContratSelection = (contratId) => {
    setSelectedContrats(prev => 
      prev.includes(contratId) 
        ? prev.filter(id => id !== contratId)
        : [...prev, contratId]
    );
  };

  const filteredBeneficiaires = beneficiaires.filter(b => 
    b.nom?.toLowerCase().includes(search.toLowerCase()) ||
    b.prenom?.toLowerCase().includes(search.toLowerCase()) ||
    b.email?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: beneficiaires.length,
    actifs: beneficiaires.filter(b => b.actif !== false).length,
    avecContrat: beneficiaires.filter(b => b.a_contrat).length
  };

  if (loading) {
    return (
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-500">Chargement des bénéficiaires...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total inscrit</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Actifs</p>
                <p className="text-3xl font-bold text-emerald-700">{stats.actifs}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-fuchsia-50 rounded-3xl border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Avec contrat</p>
                <p className="text-3xl font-bold text-purple-700">{stats.avecContrat}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des bénéficiaires */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Liste des Bénéficiaires Inscrits
            </CardTitle>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="pl-10 rounded-xl w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredBeneficiaires.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucun bénéficiaire trouvé</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredBeneficiaires.map(ben => (
                <div 
                  key={ben.id}
                  className="flex items-center justify-between p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => handleSelectBeneficiaire(ben)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-lg">
                      {ben.prenom?.[0]}{ben.nom?.[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{ben.prenom} {ben.nom}</p>
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        {ben.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {ben.email}
                          </span>
                        )}
                        {ben.zone_geographique && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {ben.zone_geographique}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      <div className="flex gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">
                          {ben.nombre_actions || 0} actions
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {ben.nombre_ateliers || 0} ateliers
                        </Badge>
                      </div>
                      {ben.a_contrat ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Contrat signé
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 text-xs">
                          <Clock className="w-3 h-3 mr-1" />
                          En parcours
                        </Badge>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog détail */}
      <Dialog open={!!selectedBeneficiaire} onOpenChange={() => setSelectedBeneficiaire(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                {selectedBeneficiaire?.prenom?.[0]}{selectedBeneficiaire?.nom?.[0]}
              </div>
              <div>
                <span className="text-xl">{selectedBeneficiaire?.prenom} {selectedBeneficiaire?.nom}</span>
                <p className="text-sm font-normal text-slate-500">
                  Fiche détaillée du bénéficiaire
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : detailData ? (
            <div className="space-y-6 py-4">
              {/* Identification du parcours — les données personnelles
                  (coordonnees, adresse, date de naissance, NIR, aides sociales)
                  ne sont pas transmises au financeur : principe de minimisation. */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Identification du parcours
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Identifiant France Travail</span>
                    <p className="font-medium">{detailData.beneficiaire?.numero_identifiant_france_travail || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Zone géographique</span>
                    <p className="font-medium">{detailData.beneficiaire?.zone_geographique || '-'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Entrée dans le dispositif</span>
                    <p className="font-medium">
                      {detailData.beneficiaire?.date_entree_dispositif || detailData.beneficiaire?.date_entree || '-'}
                      {detailData.beneficiaire?.entree_dispositif_validee === false && (
                        <span className="ml-2 text-xs text-amber-600">consentement en attente</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-500">Sortie du dispositif</span>
                    <p className="font-medium">{detailData.beneficiaire?.date_sortie || '-'}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  Les données personnelles et sensibles (coordonnees, adresse, date de
                  naissance, numero de securite sociale, aides percues) ne sont pas
                  communiquees au financeur.
                </p>
              </div>
              
              {/* Situation professionnelle */}
              <div className="bg-blue-50 rounded-xl p-4">
                <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Situation professionnelle
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-600">Situation actuelle</span>
                    <p className="font-medium text-blue-900">{detailData.beneficiaire?.situation_actuelle || '-'}</p>
                  </div>
                  <div>
                    <span className="text-blue-600">Niveau d'études</span>
                    <p className="font-medium text-blue-900">{detailData.beneficiaire?.niveau_etudes || '-'}</p>
                  </div>
                  {detailData.beneficiaire?.diplome_obtenu && (
                    <div>
                      <span className="text-blue-600">Diplôme obtenu</span>
                      <p className="font-medium text-blue-900">{detailData.beneficiaire.diplome_obtenu}</p>
                    </div>
                  )}
                  <div className="col-span-2">
                    <span className="text-blue-600">Projet professionnel</span>
                    <p className="font-medium text-blue-900">{detailData.beneficiaire?.objectif_professionnel || '-'}</p>
                  </div>
                </div>
              </div>
              
              {/* Mobilité */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Mobilité
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Permis de conduire</span>
                    <p className="font-medium">{detailData.beneficiaire?.permis_conduire ? 'Oui' : 'Non'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Véhicule</span>
                    <p className="font-medium">{detailData.beneficiaire?.vehicule ? 'Oui' : 'Non'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Mobilité géographique</span>
                    <p className="font-medium">{detailData.beneficiaire?.mobilite_geographique || '-'}</p>
                  </div>
                </div>
              </div>
              
              {/* Diagnostic de compétences */}
              {detailData.diagnostic && (
                <div className="bg-purple-50 rounded-xl p-4">
                  <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    Diagnostic de compétences
                  </h4>
                  <div className="flex items-center gap-4">
                    <Badge className={
                      detailData.diagnostic.statut === 'valide' ? 'bg-emerald-100 text-emerald-700' :
                      detailData.diagnostic.statut === 'soumis' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-700'
                    }>
                      {detailData.diagnostic.statut === 'valide' ? 'Validé' :
                       detailData.diagnostic.statut === 'soumis' ? 'En attente' : 'En cours'}
                    </Badge>
                    <span className="text-sm text-purple-600">
                      {detailData.diagnostic.nombre_competences_evaluees ?? detailData.diagnostic.evaluations?.length ?? 0} compétences évaluées
                    </span>
                  </div>
                </div>
              )}
              
              {/* Contrats */}
              {detailData.contrats && detailData.contrats.length > 0 && (
                <div className="bg-emerald-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Contrats ({detailData.contrats.length})
                    </h4>
                    {selectedContrats.length > 0 && (
                      <Button
                        size="sm"
                        onClick={handleDownloadSelected}
                        disabled={downloadingContrats}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg h-8 text-xs"
                        data-testid="download-selected-contrats"
                      >
                        <FileDown className="w-3 h-3 mr-1" />
                        {downloadingContrats ? 'Téléchargement...' : `Télécharger (${selectedContrats.length})`}
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {detailData.contrats.map((contrat, i) => (
                      <div key={contrat.id || i} className="bg-white rounded-lg p-3 text-sm flex items-center gap-3">
                        <Checkbox
                          checked={selectedContrats.includes(contrat.id)}
                          onCheckedChange={() => toggleContratSelection(contrat.id)}
                          disabled={!contrat.document_id}
                          data-testid={`select-contrat-${contrat.id}`}
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-medium">{contrat.type_contrat}</span>
                              {contrat.poste && <span className="text-slate-500 ml-2">- {contrat.poste}</span>}
                            </div>
                            <span className="text-emerald-600 text-xs">{contrat.date_debut || contrat.date_signature}</span>
                          </div>
                          {contrat.entreprise_nom && (
                            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {contrat.entreprise_nom}
                            </p>
                          )}
                        </div>
                        {contrat.document_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadContrat(contrat.id)}
                            className="h-8 w-8 p-0 hover:bg-emerald-100"
                            data-testid={`download-contrat-${contrat.id}`}
                            title="Télécharger le contrat"
                          >
                            <Download className="w-4 h-4 text-emerald-600" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Statistiques de suivi */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-amber-700">{detailData.actions?.length || 0}</p>
                  <p className="text-sm text-amber-600">Actions individuelles</p>
                </div>
                <div className="bg-indigo-50 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-indigo-700">{detailData.ateliers?.length || 0}</p>
                  <p className="text-sm text-indigo-600">Ateliers collectifs</p>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BeneficiairesFinanceur;
